/* Every report, grouped the way ERPNext groups them.
 *
 * The menu carries the dozen people open every month; this is the other two
 * hundred and twenty-six, findable without knowing which module ERPNext filed
 * them under.
 */
import { useEffect, useMemo, useState } from 'react'
import { Failed, Loading } from '@jackdesk/shell'
import { useErp } from '@jackdesk/erp-kit'
import type { ErpError } from '@jackdesk/erp-kit'

/* The end of a period is the same conversation every year: the ledger, the
 * trial balance, the two statements, and who owed what. Four screens, four
 * exports, and a zip somebody makes by hand. This is that zip, made properly.
 */
function AuditPack() {
  const erp = useErp()
  const today = new Date().toISOString().slice(0, 10)
  const yearStart = `${new Date().getFullYear() - (new Date().getMonth() < 3 ? 1 : 0)}-04-01`
  const [from, setFrom] = useState(yearStart)
  const [to, setTo] = useState(today)
  const [company, setCompany] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    erp.list('Company', { fields: ['name'], limit: 1 })
      .then((r) => setCompany(String(r.rows[0]?.name ?? '')))
      .catch(() => setCompany(''))
  }, [erp])

  return (
    <section className="card card-pad stack" style={{ gap: 10 }}>
      <div>
        <strong className="small">The audit pack</strong>
        <p className="small muted" style={{ margin: '2px 0 0', maxWidth: '62ch' }}>
          One file with the trial balance, the general ledger, both statements and who
          owed what — all over the same dates, with a note saying when it was produced
          and by whom. Nothing in it is recalculated.
        </p>
      </div>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="field" style={{ maxWidth: 190 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>From</span>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field" style={{ maxWidth: 190 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>To</span>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button className="btn btn-primary" disabled={busy || !company}
                onClick={async () => {
                  setBusy(true); setFailed(null)
                  try { await erp.downloadAuditPack(company, from, to) }
                  catch (e) { setFailed((e as Error).message) }
                  finally { setBusy(false) }
                }}>
          {busy ? 'Building…' : 'Download the pack'}
        </button>
      </div>
      {failed ? <div className="err" role="alert">{failed}</div> : null}
    </section>
  )
}

export function Reports({ onOpen }: { onOpen: (name: string) => void }) {
  const erp = useErp()
  const [rows, setRows] = useState<{ name: string; module?: string; ref_doctype?: string }[] | null>(null)
  const [error, setError] = useState<ErpError | null>(null)
  const [text, setText] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    erp.reports(undefined, controller.signal)
      .then((r) => setRows(r.reports ?? []))
      .catch((e) => { if ((e as Error).name !== 'AbortError') setError(e as ErpError) })
    return () => controller.abort()
  }, [erp])

  const groups = useMemo(() => {
    const needle = text.trim().toLowerCase()
    const by = new Map<string, typeof rows extends null ? never : NonNullable<typeof rows>>()
    for (const r of rows ?? []) {
      if (needle && !r.name.toLowerCase().includes(needle)) continue
      const key = r.module || 'Other'
      const list = by.get(key) ?? []
      list.push(r)
      by.set(key, list)
    }
    return [...by.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [rows, text])

  if (error) return <div className="page"><Failed message={error.message} requestId={error.requestId} /></div>
  if (!rows) return <div className="page"><Loading rows={6} label="Loading reports" /></div>

  const total = groups.reduce((n, [, list]) => n + list.length, 0)

  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1>Reports</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '60ch' }}>
          Every standard report, with its own filters. All of them can be downloaded as a
          spreadsheet.
        </p>
      </div>
      <div className="row" style={{ gap: 10 }}>
        <input className="input" style={{ maxWidth: 300 }} value={text} placeholder="Find a report…"
               onChange={(e) => setText(e.target.value)} aria-label="Find a report" />
        <span className="grow" />
        <span className="small muted">{total} reports</span>
      </div>

      <AuditPack />
      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {groups.map(([module, list]) => (
          <section key={module} className="card card-pad">
            <div className="row-between" style={{ marginBottom: 8 }}>
              <h3>{module}</h3><span className="chip">{list.length}</span>
            </div>
            <div className="stack" style={{ gap: 1 }}>
              {list.map((r) => (
                <button key={r.name} className="navitem" onClick={() => onOpen(r.name)}>
                  <span className="grow truncate">{r.name}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
      {total === 0 ? <p className="muted">Nothing matches “{text}”.</p> : null}
    </div>
  )
}
